import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { useContext } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { FormInputText } from "../../../../components/form/FormInputText";
import MyModal from "../../../../components/MyModal";
import { AuthContext } from "../../../../contexts/AuthContext";
import {
  LoyaltyPlan,
  loyaltyService,
} from "../../../../services/loyaltyService";
import styles from "./styles";

type Mode = "add" | "edit";

interface CategoryOption {
  id: string | number;
  name: string;
}

const LOYALTY_CATEGORIES: CategoryOption[] = [{ id: "ONE", name: "One" }];

export interface FormValues {
  email: string;
  category: string | number;
  active: boolean;
}

interface Props {
  mode?: Mode;
  categories?: CategoryOption[];
  onClose: () => void;
  onSuccess?: (message?: string) => void;
  onError?: (message?: string) => void;
  initialValues?: Partial<FormValues>;
  loyaltyId?: number;
}

export default function UserModal({
  mode = "add",
  categories = LOYALTY_CATEGORIES,
  onClose,
  onSuccess,
  onError,
  initialValues,
  loyaltyId,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const schema = yup.object({
    email: yup
      .string()
      .email(t("yup.string.email"))
      .required(t("yup.mixed.required")),
    category: yup.mixed<string | number>().required(t("yup.mixed.required")),
    active: yup.boolean().required(),
  });
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      email: initialValues?.email ?? "",
      category: initialValues?.category ?? "",
      active: initialValues?.active ?? true,
    },
  });

  const submit: SubmitHandler<FormValues> = async ({
    email,
    category,
    active,
  }) => {
    try {
      if (mode === "edit") {
        if (!loyaltyId) {
          onError?.();
          return;
        }

        await loyaltyService.update(
          loyaltyId,
          {
            loyaltyPlan: category as LoyaltyPlan,
            customerEmail: email.trim(),
            active,
          },
          tokenHeaders,
        );
      } else {
        await loyaltyService.create(
          {
            loyaltyPlan: category as LoyaltyPlan,
            customerEmail: email.trim(),
            countryCode: tokenHeaders.headers.countryCode,
          },
          tokenHeaders,
        );
      }

      onSuccess?.(
        t(mode === "add" ? "loyalty.add.success" : "loyalty.edit.success"),
      );
      onClose();
    } catch (error: any) {
      const status = error?.response?.status;

      onError?.(
        status === 409
          ? t("loyalty.duplicatedEmail")
          : error?.response?.data?.message,
      );
    }
  };

  const [email, category] = watch(["email", "category"]);
  const isFormIncomplete = !email?.trim() || !category;

  return (
    <MyModal
      title={t(
        mode === "add" ? "loyalty.modal.titleAdd" : "loyalty.modal.titleEdit",
      )}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(submit)}
    >
      <Box sx={{ mb: 3 }}>{t("loyalty.modal.description")}</Box>
      <Box sx={{ mt: 0, mb: 2 }} style={styles.identificationText}>
        {t("loyalty.modal.identification")}
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInputText
            label={t("loyalty.modal.email")}
            type="email"
            autoComplete="email"
            disabled={mode === "edit"}
            {...register("email")}
            error={errors.email}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" error={!!errors.category}>
            <InputLabel id="unique-modal-category-label">
              {t("loyalty.modal.category")}
            </InputLabel>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  labelId="unique-modal-category-label"
                  label={t("loyalty.modal.category")}
                  {...field}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.category && (
              <FormHelperText>{errors.category.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
      {mode === "edit" && (
        <>
          <Box sx={{ mt: 3, mb: 1 }} style={styles.identificationText}>
            {t("loyalty.modal.status")}
          </Box>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    name={field.name}
                    checked={!!field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    slotProps={{ input: { ref: field.ref } }}
                  />
                }
                label={t(
                  field.value
                    ? "loyalty.status.active"
                    : "loyalty.status.inactive",
                )}
              />
            )}
          />
        </>
      )}

      <Box sx={{ mt: 2 }} style={styles.subLabel}>
        {t("loyalty.modal.requiredFields")}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1rem",
          marginTop: "1rem",
          paddingTop: "1rem",
        }}
      >
        <Button
          variant="contained"
          type="submit"
          disabled={isSubmitting || isFormIncomplete}
        >
          {t(mode === "add" ? "loyalty.add.button" : "loyalty.edit.button")}
        </Button>
      </Box>
    </MyModal>
  );
}
